import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { debounce } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Modal } from '../../../../../../components/Modal';
import { SlidingMenu } from '../../../../../../components/SlidingMenu';
import * as routing from '../../../../../../constants/Routing';
import { Route } from '../../../../../../domain/route/Route';
import { Stop } from '../../../../../../domain/stop/Stop';
import { intl } from '../../../../../../helpers/IntGlobalProvider';
import Button from '../../../../../../react-components/Button';
import { Text } from '../../../../../../react-components/Text';
import { useRetrieveVehicle } from '../../../../hooks/useRetrieveVehicle';
import { AddedStops } from '../AddedStops';
import { createStop } from '../CreateStopsManually';
import { ValidateStopsBulk } from './ValidateStopsBulk';

const StyledTextArea = styled.textarea`
  &:focus-visible {
    outline: 0;
  }

  height: 100%;
  resize: none;
  font-family: Geist Variable;
  font-size: 13px;
  padding: 16px;
  border-radius: 5px;
  background-color: ${RoutalPalette.neutral05};
`;

type CreateStopsBulkSteps = `add` | `validate` | `confirm`;

interface CreateStopsProps {
  selectedRoute: Route;
}

export const CreateStopsBulk = ({ selectedRoute }: CreateStopsProps) => {
  const { data: vehicleData } = useRetrieveVehicle();

  const [addedStops, setAddedStops] = useState<string[]>([]);
  const [validatedStops, setValidatedStops] = useState<Stop[]>([]);
  const [step, setStep] = useState<CreateStopsBulkSteps>(`add`);
  const [showNotEnoughCreditsError, setShowNotEnoughCreditsError] = useState<boolean>(false);

  const navigate = useNavigate();

  const addStops = debounce((newValue: string) => {
    setAddedStops(newValue.split(`\n`));
  }, 500);

  return (
    <>
      <Modal
        id="not-enough-credits"
        open={showNotEnoughCreditsError}
        onClose={() => {
          setShowNotEnoughCreditsError(false);
        }}
        title={{
          intlId: `driver.not_enough_geocode_credit.title`,
        }}
        subtitle={{
          intlId: `driver.not_enough_geocode_credit.subtitle`,
        }}
        buttons={[
          {
            onClick: () => {
              setShowNotEnoughCreditsError(false);
            },
            intlId: `accept`,
          },
        ]}
      />

      <SlidingMenu
        id="create-stops-bulk"
        direction="down"
        background={RoutalPalette.white}
        isOpen
        headerContent={
          <Grid item container direction="row" wrap="nowrap" alignItems="center" height="70px">
            <Text variant="h6" isBold intlId="driver.stops.create_stops.add_bulk.title" />
          </Grid>
        }
        onClose={() => {
          navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
        }}
        childrenStyles={{ flexWrap: `nowrap` }}
        timeout={{ enter: 0 }}
        footerContent={
          <Grid
            style={{
              width: `100%`,
              display: `flex`,
              height: `70px`,
              zIndex: 1005,
              backgroundColor: RoutalPalette.white,
              padding: `0 14px`,
            }}
          >
            <Button
              disabled={addedStops.length === 0}
              onClick={() => {
                setStep(`validate`);
              }}
              style={{
                width: `100%`,
              }}
              intlId="driver.stops.create_stops.add_bulk.next"
            />
          </Grid>
        }
      >
        <Grid container flexDirection="row" justifyContent="flex-end" width="100%">
          <Text
            variant="small"
            intlId={
              addedStops.length === 1
                ? `driver.stops.create_stops.add_bulk.detected_stop`
                : `driver.stops.create_stops.add_bulk.detected_stops`
            }
            intlValues={{ value: addedStops.length }}
            style={{ color: RoutalPalette.secondary60 }}
          />
        </Grid>
        <VSpacer small />
        <StyledTextArea
          autoFocus
          placeholder={intl.formatMessage({ id: `driver.stops.create_stops.add_bulk.placeholder` })}
          defaultValue={addedStops.join(`\n`)}
          onChange={(changedElement) => {
            addStops(changedElement.target.value);
          }}
        />
      </SlidingMenu>

      <ValidateStopsBulk
        isOpen={step === `validate` || step === `confirm`}
        rawAddresses={addedStops}
        onClose={() => {
          setStep(`add`);
        }}
        onConfirm={(validatedAddresses) => {
          const newValidatedStops = validatedAddresses
            .filter((validatedAddress) => validatedAddress.status !== `error` && validatedAddress.location)
            .map((validatedAddress, idx) =>
              createStop(validatedAddress.location!, vehicleData?.project.fields.stop_fields ?? [], idx)
            );
          setValidatedStops(newValidatedStops);
          setStep(`confirm`);
        }}
        onNotEnoughCreditsError={() => {
          console.info(`onNotEnoughCreditsError`);
          setShowNotEnoughCreditsError(true);
        }}
      />

      {step === `confirm` && (
        <AddedStops
          selectedRoute={selectedRoute}
          addedStops={validatedStops}
          method={`Bulk`}
          onOpenAddMoreStops={() => {
            setStep(`validate`);
          }}
          onAddLocation={(location) =>
            setValidatedStops([
              ...validatedStops,
              createStop(location, vehicleData?.project.fields.stop_fields ?? [], validatedStops.length),
            ])
          }
        />
      )}
    </>
  );
};
